import prop from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Close } from '@codegrade/icons';
import { expr } from '@codegrade/utils';
import { Icon } from '@/components/Icon';
import { Popover, } from '@/components/Popover/Popover';
import * as utils from '@/utils';
const DEFAULT_CONFIRM_MODAL_HEADER = 'Are you sure?';
/**
 * The label the button will show when no label, `content` or default slot is provided.
 *
 * Defined here to use in both the prop definition, and perform a check in
 * the content node.
 */
const DEFAULT_LABEL = 'button';
/**
 * Props for the Base Button component.
 *
 * Exported for ease of re-use in other (Button-)Components.
 */
export const baseButtonProps = {
    /** The desired action this button should perform. */
    action: prop.ofFunction().required,
    /**
     * An accessible label for the button, short, specific, and action-oriented.
     *
     * MUST be provided if the text inside the button is not enough to understand
     * the action the button will perform.
     *
     * Especially important in the rare occasion that we have an icon-only button.
     */
    ariaLabel: prop(String).optional,
    /** A unique id of an external element that describes the button action. */
    ariaDescribedBy: prop(String).optional,
    /** The extra class, or classes to put on the button that we render. */
    buttonClasses: prop.ofType().optional,
    /** Which type of button this should be, default `button`. */
    type: prop.ofType().default('button'),
    /** The text content of the button. Overridden by the content and default slot. */
    label: prop(String).default(DEFAULT_LABEL),
    /** The semantic color variant of the button, default `actionable`. */
    semantic: prop.ofType().default('actionable'),
    /** The visual prominence (fill style) of the button, default 5.  */
    prominence: prop.ofType().default(5),
    /**
     * Disables the button.
     *
     * If `type: popover` is provided, we show an accessible tooltip.
     * If `type: external` is provided, we point `aria-describedby` to
     *     the external element that should explain.
     * Default: false.
     */
    disabled: prop.ofType().default(false),
    /**
     * Confirm the action of the button after clicking it once.
     *
     * Used to guard users from performing actions with dangerous consequences.
     *
     * Note: This prop will make the button
     * 1. Show the confirmation popover.
     * 2. Focus the right elements, on show and on hide.
     * 3. Disable the button while confirming,
     *    meaning do NOT use the `disabled` prop to disable while the confirm is open.
     *
     * You can use the `onToggleConfirm` event to listen for the confirm state,
     * which can be used to render custom button content.
     *
     * If false is provided, there will not be any confirm.
     * If `type: popover` is provided, we an accessible tooltip with buttons
     *  to confirm/cancel.
     * If `type: modal` is provided, we show a modal with buttons to confirm/cancel.
     * Default: false.
     */
    confirm: prop.ofType().default(false),
    /**
     * The controls of the popover in which this button is rendered, if any.
     *
     * This is needed for example to hide the confirmation popover if the button
     * is part of a dropdown and the dropdown is closed.
     */
    popover: prop.ofType().optional,
};
/**
 * The reference of the actual `button` html element we will render.
 *
 * Used for getting the element in withRef to include in the error on use of default slot.
 * Used for focussing the button after we have closed any confirm.
 */
const BUTTON_REF = 'cg-button-ref';
/** The reference for the button to cancel a confirm, used to focus when a confirm opens. */
const CONFIRM_CANCEL_BUTTON_REF = 'cg-confirm-cancel-ref';
/**
 * Button base component.
 *
 * Used as normal button in pages, and in other components that wrap
 * more logic around this button.
 */
export const Button = tsx.withPropsObject(tsx.componentFactoryOf().create({
    name: 'base-button',
    inheritAttrs: false,
    props: baseButtonProps,
    /** @returns The data for this button. */
    data() {
        /**
         * Workaround to remove Vue2 automatic application of classes to the root element
         * of a component, which we cannot disable like in Vue3.
         */
        const nativeClass = [this.$vnode.data?.class, this.$vnode.data?.staticClass];
        delete this.$vnode.data?.class;
        delete this.$vnode.data?.staticClass;
        const nativeStyle = [this.$vnode.data?.style, this.$vnode.data?.staticStyle];
        delete this.$vnode.data?.style;
        delete this.$vnode.data?.staticStyle;
        return {
            uniqueId: utils.getUniqueId(),
            hasMountedPopover: false,
            nativeClass,
            nativeStyle,
        };
    },
    computed: {
        /** @returns The unique id for this button. */
        id() {
            return this.$attrs?.id ?? `button-${this.uniqueId}`;
        },
        /** @returns The data-cy attribute for this button. */
        dataCy() {
            return this.$attrs['data-cy'] ?? this.id;
        },
        /** @returns The reference for this button, either set on this component or a default. */
        ref() {
            return this.$attrs?.ref ?? BUTTON_REF;
        },
        /** @returns The unique id for a confirm message. */
        confirmMessageId() {
            return `${this.id}-confirm-message`;
        },
        /**
         * Class for the button based on its semantic and prominence.
         *
         * Note: This must comply with the `button.css` classes that
         * we have defined for all our buttons.
         *
         * Using these classes ensures consistency and correctness.
         *
         * @returns The class to style this button.
         */
        btnSemanticProminence() {
            if (this.disabled !== false && this.disabled.style !== 'soft') {
                return `btn-disabled-${this.prominence}`;
            }
            return `btn-${this.semantic}-${this.prominence}`;
        },
        /**
         * The classes to ensure the button is styled correctly.
         *
         * Note: We must use cg-btn to apply default styling.
         *
         * @returns The classes to put on the actual `button` element.
         */
        classes() {
            return ['cg-btn', this.btnSemanticProminence, this.buttonClasses, this.nativeClass];
        },
        /**
         * @returns Whether the popover that this button is part of (if any) is currently visible.
         * If this button is not part of a popover this defaults to `true` so that we won't treat
         * the button as being hidden.
         */
        isParentPopoverVisible() {
            return this.popover?.isOpen ?? true;
        },
    },
    methods: {
        /** @returns The rendered button. */
        renderBodyNode() {
            if (this.disabled !== false) {
                return this.renderDisabledButtonNode(this.disabled);
            }
            if (this.confirm !== false) {
                return this.renderConfirmButtonNode(this.confirm);
            }
            return this.renderButtonNode();
        },
        /** @returns The normal default button with proper styling. */
        renderButtonNode() {
            return (<button {...{ attrs: this.$attrs }} role="button" id={this.id} data-cy={this.dataCy} ref={this.ref} type={this.type} style={this.nativeStyle} class={this.classes} aria-label={this.ariaLabel} aria-describedby={this.ariaDescribedBy} data-prominence={this.prominence} data-semantic={this.semantic} onClick={this.action}>
                        {this.renderContentNode()}
                    </button>);
        },
        /** @returns The content to display inside of the button. */
        renderContentNode() {
            return utils.renderScopedSlot(undefined, this.$scopedSlots.content, () => this.$slots.default ?? this.label);
        },
        /** Prevent the action from taking place. */
        preventAction(evt) {
            // Prevent a form submit and keyboard presses.
            evt.preventDefault();
            // Stop propagation to the parent element.
            evt.stopPropagation();
        },
        /**
         * Inform developers about misuse of the `Button` component.
         *
         * @param message The message informing what is wrong.
         */
        throwErrorForInvalidButtonConfiguration(message) {
            if (this.$isProduction) {
                return; // Do not crash the app on production.
            }
            this.$withRef(this.$attrs?.ref ?? BUTTON_REF, renderedButton => {
                const errorMessage = `Button ${this.id} ${message}`;
                // eslint-disable-next-line no-console
                console.error(errorMessage, renderedButton);
                // Throw so that any cypress test will crash, ensuring this
                // faulty use pattern never passes CI.
                throw Error(errorMessage);
            });
        },
        /**
         * Render a disabled button with the reason in a popover tooltip.
         *
         * Use aria-disabled instead of native disabled, so that the button
         * will still be accessible, making sure the explanation for _why_
         * it is disabled can be reached by keyboard-only users, and screen readers.
         *
         * @param disabled The reason why the button is disabled.
         * @returns The rendered disabled button with tooltip.
         */
        renderDisabledReasonPopoverNode(disabled) {
            return (<Popover variant={disabled.type} placement={disabled.placement ?? 'top-start'} class="button-wrapper contents" scopedSlots={{
                    popover: () => <span>{disabled.reason}</span>,
                    button: ({ popoverId, show, hide }) => (<button {...{ attrs: this.$attrs }} role="button" id={this.id} data-cy={this.dataCy} ref={this.ref} type={this.type} style={this.nativeStyle} class={this.classes} aria-disabled aria-label={this.ariaLabel} aria-describedby={popoverId} onBlur={hide} onFocus={show} onMouseenter={show} onMouseleave={hide} onClick={evt => {
                            show(evt);
                            this.preventAction(evt);
                        }}>
                                    {this.renderContentNode()}
                                </button>),
                }}/>);
        },
        /**
         * Render a disabled button with the reason in an external element.
         *
         * Use aria-disabled instead of native disabled, so that the button
         * will still be accessible, making sure the explanation for _why_
         * it is disabled can be reached by keyboard-only users, and screen readers.
         *
         * @param disabled The external element, explaining why the button is disabled.
         * @returns The rendered disabled button with aria-described by.
         */
        renderDisabledExternalNode(disabled) {
            return (<button {...{ attrs: this.$attrs }} role="button" id={this.id} data-cy={this.dataCy} ref={this.ref} type={this.type} style={this.nativeStyle} class={this.classes} aria-disabled aria-label={this.ariaLabel} aria-describedby={disabled.ariaDescribedby} onClick={this.preventAction}>
                        {this.renderContentNode()}
                    </button>);
        },
        /**
         * Render a disabled button, with possible reason or explanation as to why.
         *
         * @returns The rendered disabled button.
         */
        renderDisabledButtonNode(disabled) {
            return expr.match(disabled, 'type', {
                tooltip: tooltip => this.renderDisabledReasonPopoverNode(tooltip),
                external: external => this.renderDisabledExternalNode(external),
            });
        },
        /**
         * Show the confirm popover that contains the confirm for this button.
         *
         * @param evt The mouse click, or keyboard press event on the button.
         * @param show Function to show the popover.
         */
        showConfirm(evt, show) {
            show(evt);
            this.preventAction(evt);
        },
        /**
         * Hide the confirm popover that contains the confirm for this button.
         *
         * @param evt The mouse click, or keyboard press event on the button.
         * @param hide Function to hide the popover.
         */
        hideConfirm(evt, hide) {
            hide(evt);
            this.preventAction(evt);
        },
        /**
         * Handle the toggle of the confirm popover.
         *
         * Bubble up this event to the parent component.
         * Focus the right element based on whether the confirm is open.
         *
         * @param isOpen Whether the popover is open, or closed.
         */
        handleOnToggleConfirm(isOpen) {
            tsx.emitOn(this, 'onToggleConfirm', { isOpen });
            if (!this.hasMountedPopover) {
                this.hasMountedPopover = true;
                return;
            }
            if (isOpen) {
                this.$focusRef(CONFIRM_CANCEL_BUTTON_REF);
            }
            else {
                this.$focusRef(this.$attrs?.ref ?? BUTTON_REF);
            }
        },
        /**
         * Render the button that will show the confirmation popover.
         *
         * @param param0 Used for showing popover, and disabling the button if open.
         * @returns The rendered button to show the confirm popover.
         */
        renderShowConfirmButton({ show, isOpen, popoverId }) {
            return (<button {...{ attrs: this.$attrs }} role="button" id={this.id} data-cy={this.dataCy} ref={this.ref} type={this.type} style={this.nativeStyle} class={this.classes} aria-disabled={isOpen} aria-label={this.ariaLabel} aria-describedby={isOpen ? popoverId : this.ariaDescribedBy} onClick={evt => this.showConfirm(evt, show)}>
                        {this.renderContentNode()}
                    </button>);
        },
        /**
         * Render the button with a tooltip to confirm.
         *
         * @param confirm The message we wish to display in the tooltip.
         * @returns The rendered button with a tooltip to confirm.
         */
        renderConfirmPopoverNode(confirm) {
            let typ = confirm.popoverType ?? 'auto';
            // Set the popover mode to auto when the parent popover is hidden,
            // so that this popover will automatically be hidden again when the
            // parent popover is hidden.
            if (!this.isParentPopoverVisible) {
                typ = 'auto';
            }
            return (<Popover trapFocus class="button-wrapper contents" variant="dialog" placement="top" popoverType={typ} onToggle={this.handleOnToggleConfirm} scopedSlots={{
                    button: this.renderShowConfirmButton,
                    popover: ({ hide }) => (<div data-cy="button-confirm-popover" class={['flex', 'flex-col', 'gap-3', 'max-w-md']}>
                                    <div id={this.confirmMessageId}>{confirm.message}</div>
                                    <div class="flex flex-row gap-3">
                                        <Button label="Yes" prominence={2} semantic="neutral" ariaDescribedBy={this.confirmMessageId} class="flex flex-row grow justify-center" action={evt => {
                            hide(evt);
                            this.$focusRef(BUTTON_REF);
                            this.action(evt);
                        }}/>
                                        <Button label="No" ref={CONFIRM_CANCEL_BUTTON_REF} ariaDescribedBy={this.confirmMessageId} class="flex flex-row grow justify-center" action={evt => this.hideConfirm(evt, hide)}/>
                                    </div>
                                </div>),
                }}/>);
        },
        /** @returns The header for the confirm in modal. */
        renderConfirmModalHeaderNode(confirm, hide) {
            return (<div class="flex flex-row justify-between">
                        <strong>{confirm.header ?? DEFAULT_CONFIRM_MODAL_HEADER}</strong>
                        <Button semantic="neutral" prominence={1} action={evt => this.hideConfirm(evt, hide)} scopedSlots={{
                    content: () => <Icon icon={Close}/>,
                }}/>
                    </div>);
        },
        /** @returns The body of the confirm in modal. */
        renderConfirmModalBodyNode(confirm) {
            return (<div id={this.confirmMessageId}>
                        {expr.match(confirm.body, 'type', {
                    slot: () => utils.renderScopedSlot(undefined, this.$scopedSlots.confirmModalBody, () => this.throwErrorForInvalidButtonConfiguration('Used confirm with `body.type: slot but no' +
                        ' scoped slot: `confirmModalBody` was provided.')),
                    message: ({ message }) => message,
                })}
                    </div>);
        },
        /**
         * Render this button with a modal to confirm the action.
         *
         * @param confirm The message and possible header for the modal.
         * @returns The rendered button that confirms in a modal.
         */
        renderConfirmModalNode(confirm) {
            return (<Popover backdrop trapFocus class="button-wrapper contents" variant="dialog" popoverType="auto" onToggle={this.handleOnToggleConfirm} scopedSlots={{
                    button: this.renderShowConfirmButton,
                    popover: ({ hide }) => (<div data-cy="button-confirm-popover" class="flex flex-col gap-3">
                                    {this.renderConfirmModalHeaderNode(confirm, hide)}
                                    {this.renderConfirmModalBodyNode(confirm)}
                                    <div class="flex flex-row justify-between">
                                        <Button label="Confirm" prominence={2} semantic="critical" ariaDescribedBy={this.confirmMessageId} action={evt => {
                            hide(evt);
                            this.$focusRef(BUTTON_REF);
                            this.action(evt);
                        }}/>
                                        <Button label="Cancel" ref={CONFIRM_CANCEL_BUTTON_REF} ariaDescribedBy={this.confirmMessageId} action={evt => this.hideConfirm(evt, hide)}/>
                                    </div>
                                </div>),
                }}/>);
        },
        /** @returns The rendered button with a confirm. */
        renderConfirmButtonNode(confirm) {
            return expr.match(confirm, 'type', {
                dialog: dialog => this.renderConfirmPopoverNode(dialog),
                modal: modal => this.renderConfirmModalNode(modal),
            });
        },
    },
    /** @returns The rendered button base component. */
    render() {
        return this.renderBodyNode();
    },
}));
